import type { ReactNode } from "react";
import { useEffect } from "react";
import { useVertical } from "@/shared/contexts/VerticalContext";
import type { FeatureFlags } from "@/verticals/types";

interface Props {
  /** Omit to let every route through — used by the one shared route chrome, so
   *  gated and ungated routes keep the SAME element tree and the shell is never
   *  torn down between them. */
  feature?: keyof FeatureFlags;
  children: ReactNode;
  /** Path to redirect to when the feature is disabled. Defaults to /dashboard. */
  redirectTo?: string;
}

/**
 * Route-level gate that reads the current vertical's feature flags.
 * If the feature is disabled for this vertical, redirects to redirectTo
 * (default /dashboard) via the app's window.navigate router.
 *
 * Usage in App.tsx's Router switch:
 *   case "messages":
 *     return (
 *       <ProtectedRoute>
 *         <FeatureGate feature="messages">
 *           <MessagesPage />
 *         </FeatureGate>
 *       </ProtectedRoute>
 *     );
 */
export function FeatureGate({
  feature,
  children,
  redirectTo = "/dashboard",
}: Props) {
  const { verticalConfig, loading, resolved } = useVertical();
  const enabled =
    feature === undefined ||
    verticalConfig.dashboard.features[feature] === true;

  // `resolved`, not `loading`. VerticalContext clears `loading` in a finally
  // block that also runs when it bailed out early waiting for the account
  // context, so there is a window where nothing is loading and the assigned
  // course is still unknown. Deciding there sent every KAI student who
  // refreshed /messages straight back to their course.
  const known = !loading && resolved;

  useEffect(() => {
    if (!known) return;
    if (!enabled) {
      (window as any).navigate?.(redirectTo);
    }
  }, [known, enabled, redirectTo]);

  // No spinner of its own: the route chrome already holds the first paint, and
  // a second full-screen spinner here made navigation flash twice.
  if (!known) return <>{children}</>;

  if (!enabled) return null;

  return <>{children}</>;
}
